import {Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl} from '@angular/forms';
import {Partner} from '../../interface/partner';
import {ReplaySubject} from 'rxjs';
import {ListResponse} from '../../interface/response/list-response';
import {PartnerService} from '../../services/partner.service';
import {DOCUMENT} from '@angular/common';
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';

@Component({
  selector: 'app-dict-partners-chips',
  templateUrl: './dict-partners-chips.component.html',
  styleUrls: ['./dict-partners-chips.component.scss']
})
export class DictPartnersChipsComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() control: FormControl = null;
  @Input() required = false;
  @ViewChild('partnerInput') partnerInput: ElementRef<HTMLInputElement>;

  externalControl = true;
  disabled = false;
  itemsToSelect: Partner[] = [];
  selectedItems: Partner[] = [];
  itemsToSelectSubject: ReplaySubject<ListResponse<Partner>> = new ReplaySubject<ListResponse<Partner>>(1);
  selectedItemsSubject: ReplaySubject<ListResponse<Partner>> = new ReplaySubject<ListResponse<Partner>>(1);
  value?: string = null;
  selectedValue?: string = null;
  partnerFilterCtrl: FormControl;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private partnerService: PartnerService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
  ) {
    this.partnerFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl([]);
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string[]) => {
        if (val.length > 0) {
          if (val.sort().join(',') === this.selectedItems.map((partner: Partner) => partner.uuid).sort().join(',')) {
            return;
          }

          this.partnerService
            .loadNotCountedList(
              0,
              9999,
              {in$uuid: val},
              this.selectedItemsSubject
            );
        } else {
          this.selectedItems = [];
        }
      });

    this.partnerFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.partnerService.loadNotCountedList(0, 5, {
            context: val,
          }, this.itemsToSelectSubject);
        }
      });

    this.selectedItemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<Partner>) => {
        this.selectedItems = result.items;
      });

    this.itemsToSelectSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<Partner>) => {
        this.itemsToSelect = result.items;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (!value) {
      this.selectedValue = '';
    }
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue([]);
    this.writeValue([]);
    this.onChange(this.value);
  }

  remove(partner: Partner): void {
    const currentValue = this.control.value;

    const index = currentValue.indexOf(partner.uuid);
    if (index !== -1) {
      currentValue.splice(index, 1);
      this.selectedItems.splice(this.selectedItems.indexOf(partner), 1);
      this.control.setValue(currentValue);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.partnerInput.nativeElement.value = '';
    this.partnerFilterCtrl.setValue('');
    const currentValue = this.control.value;

    if (!currentValue.includes(event.option.value.uuid)) {
      currentValue.push(event.option.value.uuid);
      this.selectedItems.push(event.option.value);
      this.control.setValue(currentValue);
    }
  }
}
